import { useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { RichTextEditor } from '@/components/ui/rich-text-editor'
import { Loader2 } from 'lucide-react'
import {
  Module,
  CreateModuleDTO,
  UpdateModuleDTO,
  useModulesByCourse,
} from '@/services/admin/modules.service'
import { useAdminCourses } from '@/services/admin/courses.service'

const moduleFormSchema = z.object({
  courseId: z.coerce.number().int().positive('Selecione um curso'),
  title: z.string().min(3, 'Título deve ter no mínimo 3 caracteres').max(200),
  description: z.string().min(1, 'Descrição é obrigatória'),
  order: z.coerce.number().int().positive('Ordem deve ser um número positivo'),
  duration: z.coerce.number().int().positive().optional().or(z.literal('')),
  status: z.enum(['draft', 'published', 'archived']).default('draft'),
})

type ModuleFormValues = z.infer<typeof moduleFormSchema>

interface ModuleFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  module?: Module | null
  preselectedCourseId?: number
  onSubmit: (data: CreateModuleDTO | UpdateModuleDTO) => Promise<void>
  isSubmitting?: boolean
}

export function ModuleFormDialog({
  open,
  onOpenChange,
  module,
  preselectedCourseId,
  onSubmit,
  isSubmitting = false,
}: ModuleFormDialogProps) {
  const { data: courses } = useAdminCourses()

  // Evita sobrescrever a ordem depois que o admin já a definiu manualmente:
  // só auto-sugerimos uma vez por curso selecionado.
  const autoOrderCourseRef = useRef<number | null>(null)

  const form = useForm<ModuleFormValues>({
    resolver: zodResolver(moduleFormSchema),
    defaultValues: {
      courseId: 0,
      title: '',
      description: '',
      order: 1,
      duration: '',
      status: 'draft',
    },
  })

  // Reset form when module changes or dialog opens
  useEffect(() => {
    if (open) {
      autoOrderCourseRef.current = null
      if (module) {
        form.reset({
          courseId: module.courseId,
          title: module.title,
          description: module.description,
          order: module.order,
          duration: module.duration || '',
          status: module.status,
        })
      } else {
        form.reset({
          courseId: preselectedCourseId || 0,
          title: '',
          description: '',
          order: 1,
          duration: '',
          status: 'draft',
        })
      }
    }
  }, [module, open, preselectedCourseId, form])

  // Auto-sugere a próxima ordem livre do curso selecionado (apenas ao criar).
  // Só filtra módulos ativos, então usa max(order) + 1 (ou 1 se não houver).
  const selectedCourseId = form.watch('courseId')
  const { data: courseModules } = useModulesByCourse(
    !module && selectedCourseId > 0 ? selectedCourseId : 0
  )

  useEffect(() => {
    if (!open || module || selectedCourseId <= 0 || !courseModules) return
    if (autoOrderCourseRef.current === selectedCourseId) return

    const nextOrder =
      courseModules.length > 0 ? Math.max(...courseModules.map((m) => m.order)) + 1 : 1

    form.setValue('order', nextOrder)
    autoOrderCourseRef.current = selectedCourseId
  }, [open, module, selectedCourseId, courseModules, form])

  const handleSubmit = async (values: ModuleFormValues) => {
    const data = {
      ...values,
      duration: values.duration ? Number(values.duration) : undefined,
    }

    await onSubmit(data)
    form.reset()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{module ? 'Editar Módulo' : 'Novo Módulo'}</DialogTitle>
          <DialogDescription>
            {module ? 'Atualize as informações do módulo' : 'Preencha os dados do novo módulo'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="courseId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Curso *</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(Number(value))}
                    defaultValue={field.value?.toString()}
                    disabled={!!module}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um curso" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {courses?.map((course) => (
                        <SelectItem key={course.id} value={course.id.toString()}>
                          {course.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {module && (
                    <FormDescription>O curso não pode ser alterado após a criação</FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Título *</FormLabel>
                  <FormControl>
                    <Input placeholder="Nome do módulo" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição *</FormLabel>
                  <FormControl>
                    <RichTextEditor
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Descrição do módulo (suporta formatação)"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="order"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ordem *</FormLabel>
                    <FormControl>
                      <Input type="number" min="1" {...field} />
                    </FormControl>
                    <FormDescription>Ordem de exibição no curso</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="duration"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Duração (minutos)</FormLabel>
                    <FormControl>
                      <Input type="number" min="1" placeholder="60" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Status do módulo" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="draft">Rascunho</SelectItem>
                        <SelectItem value="published">Publicado</SelectItem>
                        <SelectItem value="archived">Arquivado</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {module ? 'Atualizar' : 'Criar'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
